import { z } from 'zod'
import { isoDateTime, uuid } from './common.js'

export const participantRole = z.enum(['advisor', 'traveller'])
export type ParticipantRole = z.infer<typeof participantRole>

export const phoneE164 = z
  .string()
  .regex(/^\+[1-9]\d{7,14}$/, 'expected E.164, e.g. +5511987654321')

/**
 * A named person on a trip.
 *
 * `displayName` is set by the ADVISOR and only by the advisor. There is no
 * request shape anywhere in this API that lets a participant name themselves,
 * and that is a security property rather than a UI preference: possession of a
 * share link proves nothing about who is holding it, so a self-set name would
 * turn the activity trail into something a stranger could forge. Ana types the
 * names; the link merely attributes.
 */
export const createParticipantRequest = z.object({
  displayName: z.string().trim().min(1).max(120),
  role: participantRole.default('traveller'),
  email: z.email().max(320).nullish(),
  phone: phoneE164.nullish(),
})
export type CreateParticipantRequest = z.infer<typeof createParticipantRequest>

export const updateParticipantRequest = createParticipantRequest.partial()
export type UpdateParticipantRequest = z.infer<typeof updateParticipantRequest>

export const participant = z.object({
  id: uuid,
  tripId: uuid,
  displayName: z.string(),
  role: participantRole,
  email: z.email().nullable(),
  phone: phoneE164.nullable(),
  createdAt: isoDateTime,
})
export type Participant = z.infer<typeof participant>

/** What a share-link holder is told about themselves. Never their contact details. */
export const participantIdentity = participant.pick({
  id: true,
  displayName: true,
  role: true,
})
export type ParticipantIdentity = z.infer<typeof participantIdentity>
